import React, { useState, useEffect } from 'react';
import api from '../../api';
import { BookOpen, Search, RefreshCw, PlayCircle, Award, X, MessageSquare } from 'lucide-react';
import '../StudentLMSSection.css'; // Reuse existing styles
import { getCourseTitle, getCourseImageUrl, handleCourseImageError } from '../../utils/coursePresentation';
import LMSFeedbackModal from '../LMSFeedbackModal';
import { useFeatures } from '../FeatureGate';

const StudentPartnerCourses = () => {
      const { isEnabled, isSubEnabled } = useFeatures();
  const canViewCourses = isSubEnabled('lms', 'lms_courses_view');
  const canSubmitFeedback = isSubEnabled('lms', 'lms_feedback_submit');
  const navigateLabsEnabled = isEnabled('navigatelabs');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [selectedMarksCourse, setSelectedMarksCourse] = useState(null);
  const [fullDescCourse, setFullDescCourse] = useState(null);
  const [courseFormsList, setCourseFormsList] = useState([]);
  const [submittedCourses, setSubmittedCourses] = useState([]);
  const [submittedData, setSubmittedData] = useState({});
  const [feedbackCourse, setFeedbackCourse] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState(null);

  const fetchData = async () => {
    if (!navigateLabsEnabled) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const [coursesRes, marksRes, schemaRes, submittedRes] = await Promise.all([
        api.get('/integrations/edvergencex/student-courses'),
        // Respect sub-feature switches: hidden subs are not even fetched.
        isSubEnabled('lms', 'lms_marks_view')
          ? api.get('/student/courses-and-marks').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        isSubEnabled('lms', 'lms_feedback_submit')
          ? api.get('/student/lms/course-feedback/forms').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        isSubEnabled('lms', 'lms_feedback_submit')
          ? api.get('/student/lms/feedback/submitted').catch(() => ({ data: { submitted_courses: [] } }))
          : Promise.resolve({ data: { submitted_courses: [] } })
      ]);

      const courseList = coursesRes.data?.data || [];
      const marksData = Array.isArray(marksRes.data) ? marksRes.data : [];

      const marksMap = {};
      marksData.forEach(item => {
        const cId = item.selection?.course_id || item.selection?.course?.externalContentRootId;
        if (cId) {
          marksMap[cId] = item.marks || [];
        }
      });

      const mergedCourses = courseList.map(c => ({
        ...c,
        test_marks: marksMap[c.externalContentRootId] || []
      }));


      setCourses(courseList);
      // Keep ALL published course feedback forms — students may be asked to
      // answer more than one for the same course.
      setCourseFormsList(schemaRes.data || []);
      setSubmittedCourses(submittedRes.data?.submitted_courses || []);
      setSubmittedData(submittedRes.data?.submitted_data || {});
    } catch (err) {
      console.error('Failed to fetch student partner courses', err);
      setError("Failed to load your assigned courses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigateLabsEnabled]);

  const handleLaunchSSO = (course) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const baseUrl = process.env.REACT_APP_NEXUS_FE_URL || 'https://develop.d3bqxb4rrd4rp8.amplifyapp.com';
    
    let launchUrl = course.launchUrl;
    if (!launchUrl) {
      launchUrl = `${baseUrl}/sso/edvergencex?courseId=${course.externalContentRootId}`;
    }
    
    if (user.email && !launchUrl.includes('login_hint=')) {
        launchUrl += (launchUrl.includes('?') ? '&' : '?') + `login_hint=${encodeURIComponent(user.email)}`;
    }
    
    window.open(launchUrl, '_blank');
  };

  const filteredCourses = courses.filter(c => 
    (c.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <header className="page-header" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.8rem', color: 'var(--text-main)', fontWeight: 800 }}>Courses</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>Exclusive courses provided by EdVergenceX for your academic batch.</p>
        </div>
        <button onClick={fetchData} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', background: 'var(--bg-card)', color: 'var(--text-main)', border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 600, width: 'fit-content' }}>
          <RefreshCw size={18} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </header>

      <div className="lms-search-container" style={{ marginBottom: '24px' }}>
        <div className="search-input-wrapper" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '0 16px', flex: 1 }}>
          <Search size={20} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search your courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ border: 'none', background: 'transparent', padding: '16px', outline: 'none', width: '100%', color: 'var(--text-main)' }}
          />
        </div>
      </div>

      {!navigateLabsEnabled ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' }}>
          <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: 'var(--text-main)' }}>NavigateLabs Integration Disabled</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>Partner courses via NavigateLabs are currently turned off for your institution.</p>
        </div>
      ) : !canViewCourses ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' }}>
          <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: 'var(--text-main)' }}>Courses are not available</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>Viewing assigned courses has been turned off for your account. Please contact your institution administrator.</p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading your courses...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>{error}</div>
      ) : filteredCourses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)' }}>
          <BookOpen size={48} style={{ color: 'var(--text-muted)', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: 'var(--text-main)' }}>No Courses Available</h3>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>You don't have any partner courses assigned to your batch at the moment.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
          {filteredCourses.map(course => (
            <div key={course.externalContentRootId} style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: '160px', overflow: 'hidden', borderBottom: '1px solid var(--border)' }}>
                <img src={getCourseImageUrl(course)} alt={getCourseTitle(course)} onError={handleCourseImageError} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem', color: 'var(--text-main)' }}>{getCourseTitle(course)}</h3>
                <div style={{ flex: 1, marginBottom: '16px' }}>
                  <p className="course-card-description" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>{course.description}</p>
                  {course.description && course.description.length > 150 && (
                    <button 
                      onClick={() => setFullDescCourse(course)}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.85rem', fontWeight: 600, marginTop: '4px' }}
                    >
                      Read More
                    </button>
                  )}
                </div>
                <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  <strong style={{ color: 'var(--text-main)' }}>Faculty Member:</strong>{' '}
                  {course.assigned_faculty_name 
                    ? `${course.assigned_faculty_name}${course.assigned_faculty?.department ? ` (${course.assigned_faculty.department})` : ''}` 
                    : 'Not assigned'}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                  {canSubmitFeedback && (
                  <button 
                    style={{ flex: 1, padding: '10px 8px', borderRadius: '8px', background: 'var(--bg-main)', color: 'var(--primary)', border: '1px solid', borderColor: 'var(--primary)', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', fontSize: '0.82rem' }}
                    onClick={() => {
                      setFeedbackCourse(course);
                      // Single form: open it directly; multiple: show the chooser
                      setFeedbackForm(courseFormsList.length === 1 ? (courseFormsList[0] || null) : null);
                    }}
                  >
                    <MessageSquare size={15} /> {submittedCourses.includes(course.externalContentRootId || course.course_id) ? 'Edit Feedback' : 'Give Feedback'}
                  </button>
                  )}
                  <button 
                    className="stu-lms-btn-start"
                    style={{ flex: 1, padding: '10px 8px', borderRadius: '8px', background: 'var(--primary)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', fontSize: '0.82rem' }}
                    onClick={() => handleLaunchSSO(course)}
                  >
                    <PlayCircle size={15} /> Start Learning
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMarksCourse && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '600px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Award size={22} style={{ color: 'var(--primary)' }} />
                <h2 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--text-main)' }}>Test Marks & Assessment Scores</h2>
              </div>
              <button onClick={() => setSelectedMarksCourse(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--text-muted)' }}>&times;</button>
            </div>
            <div style={{ padding: '24px' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', color: 'var(--text-main)' }}>{getCourseTitle(selectedMarksCourse)}</h3>
              <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                Assigned Faculty: <strong>{selectedMarksCourse.assigned_faculty_name || 'Not assigned'}</strong>
              </p>

              {(selectedMarksCourse.test_marks || []).length === 0 ? (
                <div style={{ padding: '30px 20px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-main)', borderRadius: '12px', marginTop: '16px' }}>
                  <Award size={32} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>No test marks uploaded yet by faculty for this course.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', marginTop: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-main)', color: 'var(--text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '10px 14px', borderRadius: '8px 0 0 8px', fontWeight: '700' }}>Test Name</th>
                        <th style={{ padding: '10px 14px', fontWeight: '700' }}>Marks</th>
                        <th style={{ padding: '10px 14px', borderRadius: '0 8px 8px 0', fontWeight: '700' }}>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedMarksCourse.test_marks || []).map((m, mIdx) => {
                        const pct = m.percentage || Math.round(((m.marks_obtained || 0) / (m.max_marks || 1)) * 100);
                        const gradeColor = pct >= 80 ? '#16a34a' : pct >= 70 ? '#2563eb' : '#d97706';
                        return (
                          <tr key={mIdx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '12px 14px', fontWeight: '600', color: 'var(--text-main)' }}>
                              {m.test_name}
                            </td>
                            <td style={{ padding: '12px 14px', color: 'var(--text-main)', fontWeight: '700' }}>
                              {m.marks_obtained} <span style={{ color: 'var(--text-muted)', fontWeight: '600', fontSize: '0.85em' }}>/ {m.max_marks}</span>
                            </td>
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ flex: 1, maxWidth: '100px', height: '6px', background: 'var(--border)', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: gradeColor, borderRadius: '4px' }} />
                                </div>
                                <span style={{ fontWeight: '700', color: 'var(--text-main)' }}>{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}


              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button onClick={() => setSelectedMarksCourse(null)} style={{ padding: '10px 20px', borderRadius: '8px', background: 'var(--primary)', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Full Description Modal */}
      {fullDescCourse && (
        <div className="modal-overlay" onClick={() => setFullDescCourse(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>{getCourseTitle(fullDescCourse)}</h3>
              <button className="modal-close" onClick={() => setFullDescCourse(null)}>×</button>
            </div>
            <div className="modal-content custom-scrollbar" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {fullDescCourse.description}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form chooser: shown when more than one published form exists */}
      {feedbackCourse && !feedbackForm && courseFormsList.length > 1 && canSubmitFeedback && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '520px', maxHeight: '80vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--text-main)' }}>
                Choose a Feedback Form <span style={{ color: 'var(--text-muted)', fontSize: '1rem', fontWeight: 'normal' }}>- {getCourseTitle(feedbackCourse)}</span>
              </h2>
              <button onClick={() => setFeedbackCourse(null)} style={{ background: 'var(--bg-main)', border: 'none', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', background: 'var(--bg-main)' }}>
              {courseFormsList.map(form => {
                const courseId = feedbackCourse.externalContentRootId || feedbackCourse.course_id;
                const alreadySubmitted = !!submittedData[`${courseId}|${form.id}`];
                return (
                  <button
                    key={form.id}
                    onClick={() => setFeedbackForm(form)}
                    style={{ textAlign: 'left', padding: '16px 20px', borderRadius: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--text-main)', marginBottom: '4px' }}>{form.title}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{form.questions?.length || 0} question{(form.questions?.length || 0) === 1 ? '' : 's'}</div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, flexShrink: 0,
                      background: alreadySubmitted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.12)',
                      color: alreadySubmitted ? '#10b981' : '#f59e0b'
                    }}>
                      {alreadySubmitted ? 'Submitted' : 'Pending'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {feedbackCourse && feedbackForm && canSubmitFeedback && (
        <LMSFeedbackModal
          course={feedbackCourse}
          schema={{ module_feedback: feedbackForm.questions || [] }}
          formId={feedbackForm.id}
          formTitle={feedbackForm.title}
          initialFeedback={submittedData[`${feedbackCourse.externalContentRootId || feedbackCourse.course_id}|${feedbackForm.id}`]}
          onClose={() => { setFeedbackCourse(null); setFeedbackForm(null); }}
          onSubmitSuccess={fetchData}
        />
      )}
    </div>
  );
};

export default StudentPartnerCourses;
